import { useState, useCallback } from 'react';
import { useSubscription, useQuery, gql } from '@apollo/client';
import { deepMerge } from '../utils/deepMerge';

const GET_COMMANDS = gql`
  query GetCommands($renderer: String!) {
    getCommands(renderer: $renderer) {
      renderer
      layer
      caption {
        id
        action
        data
      }
    }
  }
`;

const SUBSCRIPTION_TO_COMMAND = gql`
  subscription SubscriptionToCommand($renderer: String!) {
    subscriptionToCommand(renderer: $renderer) {
      renderer
      layer
      caption {
        id
        action
        data
      }
    }
  }
`;

export const useBrokerSubscription = (rendererId) => {
  const [actionState, setActionState] = useState({});
  const [dataState, setDataState] = useState({});

  const handleCommandUpdates = useCallback((incoming) => {
    if (!incoming) return;
    const commands = Array.isArray(incoming) ? incoming : [incoming];

    commands.forEach(cmd => {
      if (!cmd?.caption) return;
      const { id, action, data: cmdData } = cmd.caption;

      // APPLY_DATA is stateless — don't overwrite the visual state
      if (action !== undefined && action !== 'APPLY_DATA') {
        setActionState(prev => ({ ...prev, [id]: action }));
      }

      if (cmdData !== undefined) {
        setDataState(prev => {
          const currentData = prev[id] || {};
          const newData = deepMerge({}, currentData, cmdData);
          return { ...prev, [id]: newData };
        });
      }
    });
  }, []);

  // 1. Initial Fetch
  const { loading: queryLoading, error: queryError } = useQuery(GET_COMMANDS, {
    variables: { renderer: rendererId },
    skip: !rendererId,
    onCompleted: (data) => {
      handleCommandUpdates(data?.getCommands);
    }
  });

  // 2. Real-time Updates
  const { loading: subLoading, error: subError } = useSubscription(SUBSCRIPTION_TO_COMMAND, {
    variables: { renderer: rendererId },
    shouldResubscribe: true,
    skip: !rendererId,
    onData: ({ data: subscriptionData }) => {
      handleCommandUpdates(subscriptionData?.data?.subscriptionToCommand);
    }
  });

  return {
    actionState,
    dataState,
    loading: queryLoading || subLoading,
    error: queryError || subError
  };
};
